import { AnyAction, Reducer } from "redux";
import { produce } from "immer";

import { CONST_DEFAULT_SCALE_INDEX, CONST_MAX_SCALE_INDEX, CONST_MIN_SCALE_INDEX, CONST_ROOT_COMPONENT_INDEX, DEFAULT_SCROLL_POSITION, EMPTY_ARRAY } from "./constants";
import { AlignType } from "./enums";
import { IComponent, ILink } from "./interfaces";
import {
    IChangeScaleIndexAction,
    IDeleteComponentsAction,
    ILoadStateAction,
    IPicassoAction,
    IPicassoState,
    IRequestPaintingScrollPositionAction,
    ISelectAllComponentsAction,
    ISelectComponentAction,
    ISelectLinkAction,
    ISelectPointAction,
    IUnselectAllAction,
    IUnselectComponentAction,
    IUnselectLinkAction,
    IUnselectPointAction,
    IUpdateComponentsAction,
    IUpdateComponentsAlignmentAction,
    IUpdateComponentsResizeOptionAction,
    IUpdateComponentVisibilityAction,
    IUpdateLinksAction,
    Mutable,
    ScrollPosition,
    SelectablePoint,
} from "./types";
import { deletePicassoComponents, getNewCoordinates, updateSiblings } from "./utils";

export const reducer: Reducer<IPicassoState> = (state = INITIAL_STATE, action) => {
    if (!(action instanceof IPicassoAction)) { // We don't need to react on actions that aren't from Picasso.
        return state;
    }

    return produce(state, (draft: Mutable<IPicassoState>): void => {
        draft.components = components(state.components, action);
        draft.selectedComponentsIndexes = selectedComponentsIndexes(state.selectedComponentsIndexes, action, draft.components);
        draft.links = links(state.links, action);
        draft.selectedLinksIndexes = selectedLinksIndexes(state.selectedLinksIndexes, action);
        draft.selectedPoint = selectedPoint(state.selectedPoint, action);
        draft.scaleIndex = scaleIndex(state.scaleIndex, action);
        draft.requestedScrollPosition = requestedScrollPosition(state.requestedScrollPosition, action);
    });
};

const components: Reducer<readonly IComponent[]> = (state = INITIAL_STATE.components, action) => {
    if (action instanceof ILoadStateAction)
        return action.payload.components;
    else if (action instanceof IUpdateComponentsAction) {
        const { changedComps } = action.payload;

        if (changedComps.length > 1 && changedComps.some(x => state[x.componentIndex].align !== AlignType.none))
            return state;

        const components = [...state];
        changedComps.forEach(changedCompValues => {
            const originalComp = components[changedCompValues.componentIndex];
            components[changedCompValues.componentIndex] = {
                ...components[changedCompValues.componentIndex],
                width: changedCompValues.width,
                height: changedCompValues.height,
                relativeLeft: changedCompValues.x,
                relativeTop: changedCompValues.y,
            };

            const deltaWidth = changedCompValues.width - originalComp.width;
            const deltaHeight = changedCompValues.height - originalComp.height;

            if (deltaHeight !== 0 || deltaWidth !== 0) // pouze při resizování
                updateSiblings(originalComp, components, deltaWidth, deltaHeight);
        });

        return components;
    } else if (action instanceof IDeleteComponentsAction) {
        return deletePicassoComponents([...state], action.payload.componentsToDelete);
    } else if (action instanceof IUpdateComponentVisibilityAction) {
        const { componentIndex, isVisible } = action.payload;
        const components = [...state];
        components[componentIndex] = { ...components[componentIndex], isVisible };

        return components;
    } else if (action instanceof IUpdateComponentsAlignmentAction) {
        const { componentIndex, align } = action.payload;

        if (componentIndex === CONST_ROOT_COMPONENT_INDEX)
            return state;

        const components = [...state];
        const selectedComp = components[componentIndex];
        const parentComp = components[selectedComp.parent ?? CONST_ROOT_COMPONENT_INDEX];
        const siblingComps = parentComp.children.map(x => components[x]);
        components[componentIndex] = { ...selectedComp, align, ...getNewCoordinates(align, selectedComp, parentComp, siblingComps) };
        updateSiblings(selectedComp, components, -selectedComp.width, -selectedComp.height);

        return components;
    } else if (action instanceof IUpdateComponentsResizeOptionAction) {
        const { componentIndex, resizeOption, noCssHeight, noCssWidth } = action.payload;
        const component = state[componentIndex];

        if (component.resizeOption === resizeOption && component.noCssHeight === noCssHeight && component.noCssWidth === noCssWidth)
            return state;

        const components = [...state];
        components[componentIndex] = { ...component, resizeOption, noCssHeight, noCssWidth };

        return components;
    }

    return state;
};

const links: Reducer<readonly ILink[]> = (state = INITIAL_STATE.links, action) => {
    if (action instanceof ILoadStateAction)
        return action.payload.links;
    else if (action instanceof IUpdateLinksAction) {
        const links = [...state];

        action.payload.changedLinks.forEach(changedLink =>
            links[changedLink.index] = { ...links[changedLink.index], points: changedLink.newPoints },
        );

        return links;
    }

    return state;
};

const selectedComponentsIndexes = (state = INITIAL_STATE.selectedComponentsIndexes, action: AnyAction, components: readonly IComponent[]): readonly number[] => {
    if (action instanceof ILoadStateAction) {
        if (action.payload.selectedComponentIndex !== undefined)
            return [action.payload.selectedComponentIndex];
        return EMPTY_ARRAY;
    } else if (action instanceof ISelectComponentAction) {
        if (action.payload.reselect)
            return [action.payload.component.index];

        const isComponentAlreadySelected = state.includes(action.payload.component.index);
        const isComponentNotSibling = () => state.some(selectedCompId => components[selectedCompId].parent !== action.payload.component.parent);

        return !isComponentAlreadySelected && !isComponentNotSibling() ? state.concat([action.payload.component.index]) : state;
    } else if (action instanceof ISelectLinkAction)
        return action.payload.reselect ? EMPTY_ARRAY : state;
    else if (action instanceof ISelectAllComponentsAction)
        return components.filter(x => x.parent === action.payload.parentIndex).map(x => x.index);
    else if (action instanceof IUnselectComponentAction)
        return state.filter(comp => comp !== action.payload.componentIndex);
    else if (action instanceof IUnselectAllAction)
        return EMPTY_ARRAY;
    else if (action instanceof IDeleteComponentsAction)
        return EMPTY_ARRAY;

    return state;
};

const selectedLinksIndexes: Reducer<readonly number[]> = (state = INITIAL_STATE.selectedLinksIndexes, action) => {
    if (action instanceof ILoadStateAction) {
        if (action.payload.selectedLinkIndex !== undefined)
            return [action.payload.selectedLinkIndex];
        return EMPTY_ARRAY;
    } else if (action instanceof ISelectComponentAction)
        return action.payload.reselect ? EMPTY_ARRAY : state;
    else if (action instanceof ISelectLinkAction) {
        if (action.payload.reselect)
            return [action.payload.link.index];
        else if (state.every(linkId => linkId !== action.payload.link.index))
            return state.concat([action.payload.link.index]);

        return state;
    } else if (action instanceof IUnselectLinkAction)
        return state.filter(link => link !== action.payload.linkIndex);
    else if (action instanceof IUnselectAllAction)
        return EMPTY_ARRAY;

    return state;
};

const selectedPoint: Reducer<SelectablePoint | null> = (state = INITIAL_STATE.selectedPoint, action) => {
    if (action instanceof ILoadStateAction)
        return null;
    else if (action instanceof ISelectPointAction)
        return action.payload.selectedPoint;
    else if (action instanceof IUnselectPointAction)
        return null;
    else if (action instanceof IUnselectAllAction)
        return null;

    return state;
};

const scaleIndex: Reducer<number> = (state = INITIAL_STATE.scaleIndex, action) => {
    if (action instanceof IChangeScaleIndexAction) {
        const newScaleIndex = Math.round(action.payload.newScaleIndex * 10) / 10;

        if (newScaleIndex > CONST_MAX_SCALE_INDEX || newScaleIndex < CONST_MIN_SCALE_INDEX)
            return state;

        return newScaleIndex;
    }

    return state;
};

const requestedScrollPosition: Reducer<ScrollPosition> = (state = INITIAL_STATE.requestedScrollPosition, action) => {
    if (action instanceof IRequestPaintingScrollPositionAction) {
        return action.payload.requestedScrollPosition;
    }

    return state;
};

/** Počáteční stav picassa. */
const INITIAL_STATE: IPicassoState = {
    components: EMPTY_ARRAY,
    selectedComponentsIndexes: EMPTY_ARRAY,
    links: EMPTY_ARRAY,
    selectedLinksIndexes: EMPTY_ARRAY,
    selectedPoint: null,
    scaleIndex: CONST_DEFAULT_SCALE_INDEX,
    requestedScrollPosition: DEFAULT_SCROLL_POSITION,
};
